import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Crown, Gift } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  { id: "starter", name: "Starter", price: "$49/mo", features: ["Twin agent access", "Golang + PHP generation", "1 Notebook API key"] },
  { id: "studio", name: "Studio", price: "$199/mo", popular: true, features: ["Everything in Starter", "Workflow + Automation APIs", "Unlimited keys", "Priority support"] },
];

export const BillingPanel = () => {
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(null);
  const [trialing, setTrialing] = useState(false);

  useEffect(() => { checkAuth(); }, [checkAuth]); // refresh plan on open

  const startTrial = async () => {
    setTrialing(true);
    try {
      await api.post("/payments/start-trial");
      toast.success("7-day free trial activated!");
      await checkAuth();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start trial."); }
    finally { setTrialing(false); }
  };

  const trialDaysLeft = () => {
    if (user?.plan !== "trial" || !user?.trial_expires_at) return 0;
    const ms = new Date(user.trial_expires_at) - new Date();
    return Math.max(0, Math.ceil(ms / 86400000));
  };
  const canTrial = !user?.trial_used && ["free", undefined, null].includes(user?.plan);

  const subscribeLabel = (p, owned) => {
    if (owned) return "Current plan";
    if (loading === p.id) return <Loader2 className="w-4 h-4 animate-spin" />;
    return "Subscribe";
  };

  const subscribe = async (id) => {
    setLoading(id);
    try {
      const res = await api.post("/payments/checkout", { package_id: id, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout."); setLoading(null); }
  };

  return (
    <div className="h-full overflow-y-auto px-4 lg:px-10 py-8" data-testid="billing-panel">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-display text-2xl font-bold text-white">Billing & Plan</h1>
        <div className="mt-4 glass rounded-xl border border-slate-800 p-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="text-sm text-slate-400">Current plan</div>
              <div className="font-display text-lg font-semibold text-white capitalize" data-testid="current-plan">{user?.plan || "free"}</div>
            </div>
          </div>
          <span className="text-xs font-mono text-emerald-400 border border-emerald-500/30 rounded-full px-3 py-1.5">Visa · Mastercard</span>
        </div>

        {user?.plan === "trial" && (
          <div data-testid="trial-active-banner" className="mt-4 glass rounded-xl border border-emerald-500/40 p-5 flex items-center gap-3">
            <Gift className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="text-slate-100 font-medium text-sm">Free trial active</div>
              <div className="text-xs text-slate-400 mt-0.5">{trialDaysLeft()} day(s) remaining — subscribe below to keep access.</div>
            </div>
          </div>
        )}
        {canTrial && (
          <div data-testid="trial-offer-banner" className="mt-4 glass rounded-xl border border-emerald-500/40 p-5 flex items-center justify-between flex-wrap gap-4 glow-emerald">
            <div className="flex items-center gap-3">
              <Gift className="w-5 h-5 text-emerald-400" />
              <div>
                <div className="text-slate-100 font-medium text-sm">Start your 7-day free trial</div>
                <div className="text-xs text-slate-400 mt-0.5">Full access for 7 days. No card required.</div>
              </div>
            </div>
            <Button onClick={startTrial} disabled={trialing} data-testid="billing-start-trial-btn"
              className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-10 px-5">
              {trialing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start free trial"}
            </Button>
          </div>
        )}

        <div className="mt-8 grid sm:grid-cols-2 gap-5">
          {PLANS.map((p) => {
            const owned = user?.plan === p.id;
            return (
              <div key={p.id} data-testid={`billing-plan-${p.id}`}
                className={`glass rounded-2xl border p-6 ${p.popular ? "border-emerald-500/60 glow-emerald" : "border-slate-800"}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl font-semibold text-white">{p.name}</h3>
                  {p.popular && <span className="text-[10px] font-mono uppercase text-slate-950 bg-emerald-400 rounded-full px-2.5 py-1">Popular</span>}
                </div>
                <div className="mt-2 font-display text-3xl font-bold text-white">{p.price}</div>
                <ul className="mt-5 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Button onClick={() => subscribe(p.id)} disabled={loading === p.id || owned} data-testid={`billing-subscribe-${p.id}`}
                  className={`mt-6 w-full rounded-full h-11 font-semibold ${p.popular ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950" : "bg-slate-800 hover:bg-slate-700 text-white"}`}>
                  {subscribeLabel(p, owned)}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
